import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "space-y-6"
}
const _hoisted_2 = { class: "border-t pt-6" }
const _hoisted_3 = { class: "grid grid-cols-1 md:grid-cols-2 gap-3" }
const _hoisted_4 = { class: "bg-blue-50 dark:bg-blue-900/30 rounded-xl p-3" }
const _hoisted_5 = { class: "flex items-center justify-between mb-1" }
const _hoisted_6 = { class: "text-base font-semibold text-blue-600 dark:text-blue-400" }
const _hoisted_7 = { class: "bg-green-50 dark:bg-green-900/30 rounded-xl p-3" }
const _hoisted_8 = { class: "flex items-center justify-between mb-1" }
const _hoisted_9 = { class: "text-base font-semibold text-green-600 dark:text-green-400" }
const _hoisted_10 = { class: "bg-purple-50 dark:bg-purple-900/30 rounded-xl p-3" }
const _hoisted_11 = { class: "flex items-center justify-between mb-1" }
const _hoisted_12 = { class: "text-base font-semibold text-purple-600 dark:text-purple-400" }
const _hoisted_13 = { class: "bg-orange-50 dark:bg-orange-900/30 rounded-xl p-3" }
const _hoisted_14 = { class: "flex items-center justify-between mb-1" }
const _hoisted_15 = { class: "border-t pt-6" }
const _hoisted_16 = { class: "grid grid-cols-1 md:grid-cols-2 gap-4" }
const _hoisted_17 = { class: "px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white min-h-[2.75rem] flex items-center" }
const _hoisted_18 = { class: "px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white min-h-[2.75rem] flex items-center" }
const _hoisted_19 = { class: "mt-4" }
const _hoisted_20 = { class: "px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white min-h-[2.75rem] flex items-center" }
const _hoisted_21 = { class: "border-t pt-6" }
const _hoisted_22 = { class: "block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3" }
const _hoisted_23 = {
  key: 0,
  class: "text-center py-8"
}
const _hoisted_24 = {
  key: 1,
  class: "flex flex-col gap-6 max-h-[700px] overflow-y-auto pr-2 pb-6"
}
const _hoisted_25 = { class: "flex-shrink-0" }
const _hoisted_26 = { class: "bg-blue-50 dark:bg-blue-900/30 rounded-xl h-12 w-12 flex items-center justify-center" }
const _hoisted_27 = { class: "text-2xl font-bold text-blue-600 dark:text-blue-400" }
const _hoisted_28 = { class: "flex-1" }
const _hoisted_29 = { class: "flex items-center gap-2 mb-2" }
const _hoisted_30 = { class: "text-xs font-medium text-gray-500 dark:text-gray-400 tracking-wide" }
const _hoisted_31 = { class: "mb-3 text-lg text-gray-800 dark:text-gray-200 font-medium leading-relaxed" }
const _hoisted_32 = {
  key: 0,
  class: "mb-3"
}
const _hoisted_33 = { class: "space-y-2" }
const _hoisted_34 = { class: "text-gray-900 dark:text-gray-200 text-sm" }
const _hoisted_35 = { class: "mt-4" }
const _hoisted_36 = { class: "px-4 py-2 rounded-lg font-medium bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200" }
const _hoisted_37 = {
  key: 1,
  class: "text-center py-8"
}

import { ref, computed, watch } from 'vue'
import BaseModal from '@/components/common/BaseModal.vue'
import BaseButton from '@/components/common/BaseButton.vue'
import { FileText, HelpCircle } from 'lucide-vue-next'
import type { Exam } from '@/services/examService'
import type { Module } from '@/services/modules'

interface Props {
  isVisible: boolean
  exam: Exam | null
  modules?: Module[]
}

interface Emits {
  (e: 'close'): void
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ViewExamContent',
  props: {
    isVisible: { type: Boolean },
    exam: {},
    modules: {}
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const emit = __emit

// Computed properties
const modulesList = computed(() => props.modules || [])

const currentCard = ref(0)

// Methods
const getModuleName = (moduleId: number) => {
  const module = modulesList.value.find(m => m.module_id === moduleId)
  return module ? module.name : 'Unknown Module'
}

const formatDate = (dateString: string) => {
  if (!dateString) return 'N/A'
  try {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  } catch {
    return 'Invalid date'
  }
}

const getMultipleChoiceAnswer = (question: any) => {
  if (!props.exam?.answer_key || !question.options) return 'Not specified'

  const correctAnswerId = props.exam.answer_key[question.id]
  if (!correctAnswerId) return 'Not specified'

  const correctOption = question.options.find((opt: any) => opt.id === correctAnswerId)
  if (!correctOption) return 'Not specified'

  const optionIndex = question.options.findIndex((opt: any) => opt.id === correctAnswerId)
  const optionLetter = String.fromCharCode(65 + optionIndex)

  return `${optionLetter}. ${correctOption.text}`
}

const closeModal = () => {
  emit('close')
}

// Watchers
watch(() => props.isVisible, (val) => { if (val) currentCard.value = 0 }) // Reset on open


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseModal, {
    "is-visible": _ctx.isVisible,
    title: _ctx.exam?.exam_name || 'Exam Details',
    subtitle: "View exam information and content",
    icon: _unref(FileText),
    size: "4xl",
    type: "default",
    "show-footer": true,
    onClose: closeModal
  }, {
    footer: _withCtx(() => [
      _createVNode(BaseButton, {
        text: "Close",
        variant: "secondary",
        onClick: closeModal
      })
    ]),
    default: _withCtx(() => [
      (_ctx.exam)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _cache[4] || (_cache[4] = _createElementVNode("label", { class: "block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3" }, " Exam Preview ", -1)),
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("div", _hoisted_5, [
                    _cache[0] || (_cache[0] = _createElementVNode("span", { class: "text-xs font-medium text-gray-600 dark:text-gray-300" }, " Total Questions ", -1)),
                    _createElementVNode("span", _hoisted_6, _toDisplayString(_ctx.exam.exam_content?.questions?.length || 0), 1)
                  ])
                ]),
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("div", _hoisted_8, [
                    _cache[1] || (_cache[1] = _createElementVNode("span", { class: "text-xs font-medium text-gray-600 dark:text-gray-300" }, " Passing Rate ", -1)),
                    _createElementVNode("span", _hoisted_9, _toDisplayString(_ctx.exam.passing_rate) + "% ", 1)
                  ])
                ]),
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("div", _hoisted_11, [
                    _cache[2] || (_cache[2] = _createElementVNode("span", { class: "text-xs font-medium text-gray-600 dark:text-gray-300" }, " Time Limit ", -1)),
                    _createElementVNode("span", _hoisted_12, _toDisplayString(_ctx.exam.time_limit ? `${_ctx.exam.time_limit} min` : 'No limit'), 1)
                  ])
                ]),
                _createElementVNode("div", _hoisted_13, [
                  _createElementVNode("div", _hoisted_14, [
                    _cache[3] || (_cache[3] = _createElementVNode("span", { class: "text-xs font-medium text-gray-600 dark:text-gray-300" }, " Status ", -1)),
                    _createElementVNode("span", {
                      class: _normalizeClass(_ctx.exam.exam_status === 1 ? 'badge-success' : 'badge-warning')
                    }, _toDisplayString(_ctx.exam.exam_status === 1 ? 'Active' : 'Draft'), 3)
                  ])
                ])
              ])
            ]),
            _createElementVNode("div", _hoisted_15, [
              _createElementVNode("div", _hoisted_16, [
                _createElementVNode("div", null, [
                  _cache[5] || (_cache[5] = _createElementVNode("label", { class: "block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2" }, " Exam Name ", -1)),
                  _createElementVNode("div", _hoisted_17, _toDisplayString(_ctx.exam.exam_name), 1)
                ]),
                _createElementVNode("div", null, [
                  _cache[6] || (_cache[6] = _createElementVNode("label", { class: "block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2" }, " Created Date ", -1)),
                  _createElementVNode("div", _hoisted_18, _toDisplayString(formatDate(_ctx.exam.created_at)), 1)
                ])
              ]),
              _createElementVNode("div", _hoisted_19, [
                _cache[7] || (_cache[7] = _createElementVNode("label", { class: "block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2" }, " Module ", -1)),
                _createElementVNode("div", _hoisted_20, _toDisplayString(getModuleName(_ctx.exam.module_id)), 1)
              ])
            ]),
            _createElementVNode("div", _hoisted_21, [
              _createElementVNode("label", _hoisted_22, " Questions (" + _toDisplayString(_ctx.exam.exam_content?.questions?.length || 0) + ") ", 1),
              (!_ctx.exam.exam_content?.questions?.length)
                ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                    _createVNode(_unref(HelpCircle), { class: "h-16 w-16 text-gray-400 mx-auto mb-4" }),
                    _cache[8] || (_cache[8] = _createElementVNode("p", { class: "text-gray-500 dark:text-gray-400" }, " This exam doesn't contain any questions yet. ", -1))
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_24, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.exam.exam_content.questions, (question, index) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: question.id,
                        class: "bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 flex gap-4 items-start"
                      }, [
                        _createElementVNode("div", _hoisted_25, [
                          _createElementVNode("div", _hoisted_26, [
                            _createElementVNode("span", _hoisted_27, _toDisplayString(index + 1), 1)
                          ])
                        ]),
                        _createElementVNode("div", _hoisted_28, [
                          _createElementVNode("div", _hoisted_29, [
                            _createElementVNode("span", _hoisted_30, _toDisplayString(question.type === 'multiple_choice'
                      ? 'Multiple Choice'
                      : question.type === 'true_false'
                      ? 'True or False'
                      : 'Essay'), 1)
                          ]),
                          _createElementVNode("div", _hoisted_31, _toDisplayString(question.question), 1),
                          (question.type === 'multiple_choice' && question.options)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_32, [
                                _createElementVNode("ul", _hoisted_33, [
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(question.options, (option, optIndex) => {
                                    return (_openBlock(), _createElementBlock("li", {
                                      key: optIndex,
                                      class: "flex items-center gap-2"
                                    }, [
                                      _createElementVNode("span", {
                                        class: _normalizeClass(["inline-flex items-center justify-center h-6 w-6 rounded-full border border-gray-300 dark:border-gray-600 text-xs font-semibold", {
                        'bg-blue-500 text-white border-blue-500': _ctx.exam.answer_key?.[question.id] === option.id,
                        'bg-gray-100 dark:bg-gray-800': _ctx.exam.answer_key?.[question.id] !== option.id
                      }])
                                      }, _toDisplayString(String.fromCharCode(65 + optIndex)), 3),
                                      _createElementVNode("span", _hoisted_34, _toDisplayString(option.text), 1)
                                    ]))
                                  }), 128))
                                ])
                              ]))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", _hoisted_35, [
                            _cache[9] || (_cache[9] = _createElementVNode("label", { class: "block text-xs font-semibold text-green-600 dark:text-green-400 mb-1" }, " Correct Answer ", -1)),
                            _createElementVNode("div", _hoisted_36, [
                              (question.type === 'multiple_choice')
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                    _createTextVNode(_toDisplayString(getMultipleChoiceAnswer(question)), 1)
                                  ], 64))
                                : (question.type === 'true_false')
                                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                      _createTextVNode(_toDisplayString(_ctx.exam.answer_key?.[question.id]), 1)
                                    ], 64))
                                  : (question.type === 'essay')
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                        _createTextVNode(_toDisplayString(_ctx.exam.answer_key?.[question.id] || 'No sample answer provided'), 1)
                                      ], 64))
                                    : _createCommentVNode("", true)
                            ])
                          ])
                        ])
                      ]))
                    }), 128))
                  ]))
            ])
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_37, [...(_cache[10] || (_cache[10] = [
            _createElementVNode("div", { class: "animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" }, null, -1),
            _createElementVNode("p", { class: "text-gray-500 dark:text-gray-400" }, "Loading exam details...", -1)
          ]))]))
    ]),
    _: 1
  }, 8, ["is-visible", "title", "icon"]))
}
}

})